import { createApi } from '@reduxjs/toolkit/query/react'

import { customToast } from '@/hooks'
import { baseQueryWithAuth } from '@/stores/redux/config'
import { useAuthStore } from '@/stores/zustand/auth-store'

import { convertBooleanToNumber } from '@/utils/convertTypes'

import type {
  IChangePasswordData,
  IReferralRequest,
  ISetPasswordData,
  ISettingItem,
  ISimpleResponse,
  ITimezone,
  IUpdateUserSettingsData,
  IUploadLink,
  IUser,
} from '@/interfaces'

export const profileApi = createApi({
  baseQuery: baseQueryWithAuth,
  reducerPath: 'profileApi',
  endpoints: (build) => ({
    getProfile: build.query<IUser, void>({
      query: () => ({
        url: '/profile',
        method: 'GET',
      }),
      transformResponse: (baseQueryReturnValue: { data: IUser }) => {
        useAuthStore.getState().handleSetUser(baseQueryReturnValue.data)
        return baseQueryReturnValue.data
      },
      transformErrorResponse: (baseQueryReturnValue: any) => {
        if (baseQueryReturnValue.data.type) {
          return baseQueryReturnValue.data
        }
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),

    getTimezone: build.query<ITimezone, void>({
      query: () => ({
        url: '/timezone',
        method: 'GET',
      }),
      transformResponse: (baseQueryReturnValue: ITimezone) => {
        return baseQueryReturnValue
      },
      transformErrorResponse: (baseQueryReturnValue: any) => {
        if (baseQueryReturnValue.data.type) {
          return baseQueryReturnValue.data
        } else {
          customToast(baseQueryReturnValue.data.message, 'error')
        }
      },
    }),

    getUploadLink: build.mutation<IUploadLink, { filePath: string }>({
      query: ({ filePath }) => ({
        url: '/files/get-upload-url',
        params: { path: filePath },
        method: 'POST',
      }),
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),

    updateUser: build.mutation<IUser, Partial<IUser>>({
      query: (body) => ({
        url: '/profile',
        method: 'PUT',
        body,
      }),
      transformResponse: (baseQueryReturnValue: { data: IUser }) => baseQueryReturnValue.data,
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
      async onQueryStarted(_, { queryFulfilled }) {
        const { data: user } = await queryFulfilled
        useAuthStore.getState().handleSetUser(user)
      },
    }),

    setPassword: build.mutation<ISimpleResponse, ISetPasswordData>({
      query: (body) => ({
        url: '/user/set-password',
        method: 'POST',
        body,
      }),
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),

    changePassword: build.mutation<ISimpleResponse, IChangePasswordData>({
      query: (body) => ({
        url: '/user/change-password',
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),

    getUserSettings: build.query<ISettingItem[], void>({
      query: () => ({
        url: '/user/settings',
        method: 'GET',
      }),
      transformResponse: (baseQueryReturnValue: { data: ISettingItem[] }) =>
        baseQueryReturnValue.data,
    }),

    deleteUser: build.mutation<ISimpleResponse, void>({
      query: () => ({
        url: '/user',
        method: 'DELETE',
      }),
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),

    updateUserSettings: build.mutation<ISettingItem[], IUpdateUserSettingsData>({
      query: (body) => ({
        url: '/user/settings',
        method: 'PUT',
        body: {
          ...body,
          ...(body.hpi_merging !== undefined && {
            hpi_merging: convertBooleanToNumber(body.hpi_merging),
          }),
          ...(body.hide_recording_permission_prompt !== undefined && {
            hide_recording_permission_prompt: convertBooleanToNumber(
              body.hide_recording_permission_prompt,
            ),
          }),
          ...(body.note_auto_submission_to_ehr !== undefined && {
            note_auto_submission_to_ehr: convertBooleanToNumber(body.note_auto_submission_to_ehr),
          }),
          ...(body.default_note_generation_mode !== undefined && {
            default_note_generation_mode: body.default_note_generation_mode,
          }),
          ...(body.smart_formatting !== undefined && {
            smart_formatting: convertBooleanToNumber(body.smart_formatting),
          }),
        },
      }),
      transformResponse: (baseQueryReturnValue: { data: ISettingItem[] }) =>
        baseQueryReturnValue.data,
    }),
    updateNoteSetupStep: build.mutation<IUser, void>({
      query: () => {
        return {
          url: '/user/note-setup/step',
          method: 'POST',
        }
      },
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),

    refreshSchedule: build.mutation<ISimpleResponse, void>({
      query: () => ({
        url: '/merlin/refresh-schedule',
        method: 'POST',
      }),
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),

    joinReferralProgram: build.mutation<string, IReferralRequest>({
      query: (body) => ({
        url: '/user/join-referral',
        method: 'POST',
        body,
      }),
      transformResponse: (baseQueryReturnValue: { link: string }) => {
        return baseQueryReturnValue.link
      },
      transformErrorResponse: (baseQueryReturnValue: any) => {
        customToast(baseQueryReturnValue.data.message, 'error')
      },
    }),
  }),
})

export const {
  useGetProfileQuery,
  useGetTimezoneQuery,
  useLazyGetProfileQuery,
  useUpdateUserMutation,
  useSetPasswordMutation,
  useGetUploadLinkMutation,
  useChangePasswordMutation,
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
  useDeleteUserMutation,
  useUpdateNoteSetupStepMutation,
  useRefreshScheduleMutation,
  useJoinReferralProgramMutation,
} = profileApi
